import { formatDistanceToNowStrict, intlFormat } from 'date-fns';
import { pick } from 'lodash';
import { getStoredSocketMetaSid, setStoredSocketMetaSid } from 'shared/store/storage';
import { Moreable, Page } from 'types';
import { PUBLIC_HOST_URL } from 'shared/settings';

export function isNumeric(n: any) {
  return !isNaN(parseFloat(n)) && isFinite(n);
}

export async function sleep(ms: number) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export const durationFrom = (from: number) => {
  return from ? formatDuration(Math.max(Date.now() - from, 0)) : '';
};

export const relativeTime = (time: number) => {
  // increase by a second to eliminate 'in 0 seconds'
  const mod = time > Date.now() ? 1000 : -1000;
  return time ? formatDistanceToNowStrict(time + mod, { addSuffix: true }) : '';
};

export const formatDuration = (duration: number) => {
  const date = new Date(duration);
  const hours = date.getUTCHours();
  const minutes = date.getUTCMinutes();
  const seconds = date.getUTCSeconds();
  return hours > 0
    ? `${hours}:${minutes < 10 ? '0' : ''}${minutes}:${seconds < 10 ? '0' : ''}${seconds}`
    : `${minutes}:${seconds < 10 ? '0' : ''}${seconds}`;
};

export const formatDate = (date: number) => {
  return date
    ? intlFormat(new Date(date), {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      })
    : '';
};

export const formatDateLong = (date: number) => {
  return date
    ? intlFormat(new Date(date), {
        month: 'long',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      })
    : '';
};

export const formatDateShort = (date: number | string) => {
  return date
    ? intlFormat(new Date(date), {
        month: 'short',
        day: 'numeric',
        hour: 'numeric',
        minute: 'numeric',
      })
    : '';
};

export const formatTime = (date: number) => {
  return date
    ? intlFormat(new Date(date), {
        hour: 'numeric',
        minute: 'numeric',
      })
    : '';
};

export const formatDateWithDaySuffix = (date?: string) => {
  if (!date) return '';

  const dateObj = new Date(date);
  const day = dateObj.getDate();
  const month = dateObj.toLocaleString('default', { month: 'short' });
  const year = dateObj.getFullYear();

  const nthNumber = (number: number) => {
    if (number > 3 && number < 21) return 'th';
    switch (number % 10) {
      case 1:
        return 'st';
      case 2:
        return 'nd';
      case 3:
        return 'rd';
      default:
        return 'th';
    }
  };

  return `${month} ${day}${nthNumber(day)}, ${year}`;
};

/**
 * Format number of seconds to timer, e.g. 67 -> "1:07"
 */
export function formatSecondsToTimer(seconds: number) {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60)
    .toString()
    .padStart(2, '0');
  return `${mins}:${secs}`;
}

// clone and filter keys by keySource object's keys
export function cleanClone<T extends object>(keySource: T, target: Partial<T>) {
  const sourceKeys = Object.keys(keySource);
  const fullClone = { ...(target as object) };
  const clone = pick(fullClone, sourceKeys);
  return clone as T;
}

export function urlToPublic(url: string) {
  let withPublicHost = url.match(/^https?:/) ? url : PUBLIC_HOST_URL + url;
  if (process.env.NODE_ENV === 'development' && PUBLIC_HOST_URL) {
    withPublicHost = withPublicHost.replace(/^http:\/\/localhost(:\d+)/, PUBLIC_HOST_URL);
  }
  return withPublicHost;
}

export function makeInitialMoreableState<T>(): Moreable<T> {
  return {
    pages: [] as T[][],
    hasMore: false,
    parentId: null,
    page: 1,
    pageSize: 0,
    total: 0,
    search: '',
    sort: '',
    filters: [],
    hasFetched: false,
    isFetching: false,
    fetchError: null,
    fetchTime: 0,
  };
}

export function pendingMoreablePage<T>(
  state: Moreable<T>,
  params: Partial<Moreable<T>>,
): Moreable<T> {
  let newState: Partial<Moreable<T>> = {
    isFetching: true,
    page: state.page + 1,
    fetchError: '',
  };
  // if we ever use search, filter or sort we'll want to check those here
  if (state.parentId !== params.parentId) {
    // reset
    newState = {
      ...newState,
      parentId: params.parentId,
      pages: [],
      page: 1,
      pageSize: 0,
      total: 0,
      hasFetched: false,
    };
  }
  return {
    ...state,
    ...newState,
  };
}

export function fulfilledMoreablePage<T>(
  state: Moreable<T>,
  serverPage: Page<T>,
): Moreable<T> {
  const { total, pageSize, page, items } = serverPage;
  let pages = [...state.pages, items];
  if (page !== state.pages.length + 1) {
    if (page === 1) {
      pages = [items];
    }
  }
  const hasMore = page * pageSize < total;
  return {
    ...state,
    total,
    pageSize,
    page,
    pages,
    hasMore,
    hasFetched: true,
    isFetching: false,
    fetchTime: Date.now(),
  };
}

export function loadScript(src: string) {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script');
    s.src = src;
    s.onload = resolve;
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

export function validURL(str: string) {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3})|localhost)' + // OR ip (v4) address or localhost
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // fragment locator
  return !!pattern.test(str);
}

export function removeAllChildNodes(parent: HTMLElement) {
  while (parent.firstChild) {
    parent.removeChild(parent.firstChild);
  }
}

// geometry

export const getPercentageChange = (oldNumber: number, newNumber: number) => {
  const decreaseValue = oldNumber - newNumber;
  return (decreaseValue / oldNumber) * 100;
};

export const findSmallestSafeMultiplier = (
  theirWidth: number,
  theirHeight: number,
  wrapperMaxWidth: number,
  wrapperMaxHeight: number,
) => {
  let scaleTransform = 1;

  const percentageLargerTheirWidth = getPercentageChange(theirWidth, wrapperMaxWidth);
  const percentageLargerTheirHeight = getPercentageChange(theirHeight, wrapperMaxHeight);

  // width is larger
  if (percentageLargerTheirWidth > percentageLargerTheirHeight) {
    scaleTransform = wrapperMaxWidth / theirWidth;
  } else {
    // height is largest
    scaleTransform = wrapperMaxHeight / theirHeight;
  }
  const computedAlternativeWidth = scaleTransform * theirWidth;
  const computedAlternativeHeight = scaleTransform * theirHeight;

  return { scaleTransform, computedAlternativeWidth, computedAlternativeHeight };
};

// media streams

export function stopBothVideoAndAudio(stream: MediaStream) {
  stream.getTracks().forEach(track => {
    if (track.readyState === 'live') {
      track.stop();
    }
  });
}

export function stopVideoOnly(stream: MediaStream) {
  stream.getTracks().forEach(track => {
    if (track.readyState === 'live' && track.kind === 'video') {
      track.stop();
    }
  });
}

export function stopAudioOnly(stream: MediaStream) {
  stream.getTracks().forEach(track => {
    if (track.readyState === 'live' && track.kind === 'audio') {
      track.stop();
    }
  });
}

export const disableReactDevToolsInProduction = (): void => {
  if (process.env.NODE_ENV === 'production') {
    const noop = (): void => undefined;
    const DEV_TOOLS = (window as any).__REACT_DEVTOOLS_GLOBAL_HOOK__;

    if (typeof DEV_TOOLS === 'object') {
      for (const [key, value] of Object.entries(DEV_TOOLS)) {
        if (key === 'renderers') {
          DEV_TOOLS[key] = new Map();
        } else {
          DEV_TOOLS[key] = typeof value === 'function' ? noop : null;
        }
      }
    }
  }
};

// Both taken from servicebell widget utils.js, used for sb in sb fixes
export const inIframe = () =>
  window.frameElement && window.top && window.top.document.body.id === 'svb-top-body';

export const getMySbid = () => {
  const key = 'servicebelltoken';
  const cookie = document.cookie || '';
  const value = cookie.match('(^|;)\\s*' + key + '\\s*=\\s*([^;]+)');
  return value ? value.pop() : '';
};

export const isPlayNoInteractionError = (error: Error) => {
  return error.name === 'NotAllowedError' && error.message.indexOf('interact') >= 0;
};

export const ephemeralFlag = (
  expireAfterMs: number,
  onTimeout = (): any => undefined,
) => {
  let to = 0;
  return {
    value: () => !!to,
    setTrue: () => {
      to = window.setTimeout(() => {
        onTimeout();
        to = 0;
      }, expireAfterMs);
    },
    clear: () => {
      clearTimeout(to);
      to = 0;
    },
  };
};

/**
 * Gets the duration of a video element. Accounts for an issue where some browsers
 * report duration as Infinity by forcing the video to discover its duration.
 * Has a side-effect of setting the `currentTime = 0` if it has to discover.
 */
export const getVideoDuration = (vidEl: HTMLVideoElement): Promise<number> => {
  return new Promise(resolve => {
    if (!vidEl.duration) {
      return resolve(0);
    }
    if (vidEl.duration && Number.isFinite(vidEl.duration)) {
      return resolve(vidEl.duration);
    }
    const timeUpdateHandler = () => {
      resolve(vidEl.duration);
      vidEl.removeEventListener('timeupdate', timeUpdateHandler);
    };
    vidEl.addEventListener('timeupdate', timeUpdateHandler);
    vidEl.currentTime = 1e101;
  });
};

// https://gist.github.com/hyamamoto/fd435505d29ebfa3d9716fd2be8d42f0.js
/**
 * Returns a hash code for a string.
 * (Compatible to Java's String.hashCode())
 *
 * The hash code for a string object is computed as
 *     s[0]*31^(n-1) + s[1]*31^(n-2) + ... + s[n-1]
 * using number arithmetic, where s[i] is the i th character
 * of the given string, n is the length of the string,
 * and ^ indicates exponentiation.
 * (The hash value of the empty string is zero.)
 *
 * @param {string} s a string
 * @return {number} a hash code value for the given string.
 */
export const stringHashCode = function (s: string) {
  const l = s.length;
  let h = 0,
    i = 0;
  if (l > 0) while (i < l) h = ((h << 5) - h + s.charCodeAt(i++)) | 0;
  return h;
};

export const getBrowserSessionSocketMetaSid = () => {
  let msid = getStoredSocketMetaSid();
  if (!msid) {
    msid = `msid-${(Math.random() + 1).toString(36).substring(4)}`;
    setStoredSocketMetaSid(msid);
  }
  return msid;
};

// expose on window for debugging
export const __expose = (obj: Record<string, any>) => {
  const cur = (window as any).__sbfexpose__ || {};
  (window as any).__sbfexpose__ = { ...cur, ...obj };
};

/**
 * Wait for an async value without a timer.
 *
 * Example:
 *
 * const myAwaitedValue = awaitValue<MyValueType>();
 * ...
 * const myValue = await myAwaitedValue.value;
 * ...
 * myAwaitedValue.waiter.set(delayedValue);
 */
class AwaitValueWrapper<T> {
  value = null as null | T;
  constructor(readonly callback: (w: AwaitValueWrapper<T>) => void) {}
  set(v: T) {
    this.value = v;
    this.callback(this);
  }
  get() {
    return this.value as T;
  }
}
export const awaitValue = <T>() => {
  let waiter = null as null | AwaitValueWrapper<T>;
  const value = new Promise<T>(res => {
    const cb = (s: AwaitValueWrapper<T>) => res(s.get());
    waiter = new AwaitValueWrapper(cb);
  });
  return { waiter, value };
};

export const swap = (array: number[], from: number, to: number): number[] => {
  const arr = [...array];
  const item = array[from];
  arr.splice(from, 1);
  arr.splice(to, 0, item);
  return arr;
};

export const formatRevenue: (num: number) => string | undefined = num => {
  if (typeof num !== 'number' || isNaN(num)) {
    return;
  }

  const absNum = Math.abs(num);
  const suffixes = ['', 'k', 'M', 'B', 'T'];

  const tier = Math.floor(Math.log10(absNum) / 3);

  if (tier === 0) return num.toString();

  const suffix = suffixes[tier];
  const scale = Math.pow(10, tier * 3);
  const scaled = num / scale;

  const formatted = scaled.toString().replace(/\.0+$|(\.\d+?)0+$/, '$1'); // Remove trailing zeros

  return formatted + suffix;
};
